// src/features/task/TaskWatchers.tsx
// Watchers panel for the task detail rail: watch/unwatch self, and add/remove
// any watcher via UserPicker. Wraps AddTaskWatcher / RemoveTaskWatcher.

import { useState } from 'react';
import { observer } from '@react';
import { useTask, useI18n, useAuth } from '@react/hooks';
import {
  Button,
  Card,
  CardContent,
  Alert,
} from '@react/components';
import { UserPicker, type UserPickerScope } from '@/components/UserPicker';
import { ActorReference } from '@/features/references/ActorReference';
import { errMsg } from '@/features/task/panelHelpers';
import type { Actor } from '@core/types/actor';

interface TaskWatchersProps {
  taskId: string;
  peopleScope?: UserPickerScope;
  watchers: Actor[];
  editable?: boolean;
  /** Render without the Card chrome — the host groups and frames it. */
  bare?: boolean;
  onChanged?: () => void;
}

export const TaskWatchers = observer(({ taskId, peopleScope, watchers, editable = true, bare = false, onChanged }: TaskWatchersProps) => {
  const store = useTask();
  const auth = useAuth();
  const { t } = useI18n();

  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  const currentUserId = auth.activeProfileId || '';
  const isWatching = !!currentUserId && watchers.some((w) => w.entity?.id === currentUserId);

  const addWatcher = async (userId: string, displayName: string) => {
    if (!userId || watchers.some((w) => w.entity?.id === userId)) return;
    setBusyId(userId);
    setError(null);
    try {
      await store.AddTaskWatcher(taskId, {
        task: { id: taskId },
        watcher: { entity: { id: userId }, display_name: displayName || undefined },
      } as any);
      setAddOpen(false);
      onChanged?.();
    } catch (e) {
      setError(errMsg(e, t('task.watchControl.addError')));
    } finally {
      setBusyId(null);
    }
  };

  const removeWatcher = async (userId: string) => {
    setBusyId(userId);
    setError(null);
    try {
      await store.RemoveTaskWatcher(taskId, {
        task: { id: taskId },
        watcher: { entity: { id: userId } },
      } as any);
      onChanged?.();
    } catch (e) {
      setError(errMsg(e, t('task.watchControl.removeError')));
    } finally {
      setBusyId(null);
    }
  };

  const toggleSelfWatch = () => {
    if (!currentUserId) return;
    const name = auth.user?.username || auth.user?.email || t('task.watching');
    if (isWatching) removeWatcher(currentUserId);
    else addWatcher(currentUserId, name);
  };

  const body = (
    <>
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
          {t('task.watchers')}
        </p>
        {editable && currentUserId && (
          <Button
            size="sm"
            variant={isWatching ? 'secondary' : 'ghost'}
            isLoading={busyId === currentUserId}
            onClick={toggleSelfWatch}
          >
            {isWatching ? t('task.unwatch') : t('task.watch')}
          </Button>
        )}
      </div>

      {error && <Alert variant="error">{error}</Alert>}

      {watchers.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('task.watchControl.empty')}</p>
      ) : (
        <div className="space-y-1.5">
          {watchers.map((w, idx) => {
            const wid = w.entity?.id || '';
            return (
              <div key={wid || idx} className="flex items-center justify-between gap-2 group">
                <div className="flex items-center gap-2 min-w-0">
                  <ActorReference actor={w} className="text-sm" />
                </div>
                {editable && wid && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-destructive shrink-0 opacity-0 group-hover:opacity-100"
                    isLoading={busyId === wid}
                    onClick={() => removeWatcher(wid)}
                  >
                    {t('common.remove')}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {editable && (
        addOpen ? (
          <div className="space-y-2 rounded-md border border-border p-2">
            <UserPicker
              value=""
              onChange={addWatcher}
              scope={peopleScope}
              valueKind="profile"
              placeholder={t('task.addWatcher')}
            />
            <Button size="sm" variant="ghost" onClick={() => setAddOpen(false)}>
              {t('common.cancel')}
            </Button>
          </div>
        ) : (
          <Button size="sm" variant="secondary" onClick={() => setAddOpen(true)}>
            {t('task.addWatcher')}
          </Button>
        )
      )}
    </>
  );

  if (bare) {
    return <div className="space-y-3">{body}</div>;
  }

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        {body}
      </CardContent>
    </Card>
  );
});

export default TaskWatchers;
